import { useEffect, useState } from 'react';
import { api } from '@/api';
import { MOOD_EMOJIS, MOOD_LABELS } from '@ultisim/shared';
import { format, addWeeks, subWeeks, startOfWeek, endOfWeek } from 'date-fns';
import { getInitials } from '@ultisim/shared';

interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  hoursThisWeek: number;
  timesheetStatus: string;
  submittedAt: string | null;
  mood: number | null;
  checkin: {
    wins: string | null;
    challenges: string | null;
    helpNeeded: string | null;
  } | null;
  hasCheckedIn: boolean;
}

interface TeamData {
  weekStart: string;
  weekEnd: string;
  team: TeamMember[];
}

export default function AdminTeamPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [teamData, setTeamData] = useState<TeamData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });

  useEffect(() => {
    const loadTeam = async () => {
      setIsLoading(true);
      try {
        const dateStr = format(currentDate, 'yyyy-MM-dd');
        const data = await api.get<TeamData>('/admin/team', { date: dateStr });
        setTeamData(data);
      } catch (err) {
        console.error('Failed to load team data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadTeam();
  }, [currentDate]);

  const getRowColor = (member: TeamMember) => {
    if (member.timesheetStatus === 'approved') return 'border-l-4 border-l-green-500';
    if (member.timesheetStatus === 'submitted') return 'border-l-4 border-l-blue-500';
    if (member.hoursThisWeek > 0) return 'border-l-4 border-l-amber-500';
    return 'border-l-4 border-l-red-500/50';
  };

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      approved: 'badge-success',
      submitted: 'badge-primary',
      rejected: 'badge-danger',
      draft: 'badge-gray',
      not_started: 'badge-gray',
    };
    return <span className={`badge ${map[status] || 'badge-gray'}`}>{status.replace('_', ' ')}</span>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Team Overview</h1>
        <p className="text-dark-400 mt-1">Weekly status at a glance — click a row for details</p>
      </div>

      {/* Week navigation */}
      <div className="card">
        <div className="flex items-center justify-between">
          <button onClick={() => setCurrentDate(d => subWeeks(d, 1))} className="btn-ghost">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>
          <div className="text-center">
            <p className="text-lg font-semibold text-white">
              {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
            </p>
            <button onClick={() => setCurrentDate(new Date())} className="text-sm text-primary-400 hover:text-primary-300">
              Go to this week
            </button>
          </div>
          <button onClick={() => setCurrentDate(d => addWeeks(d, 1))} className="btn-ghost">
            Next
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {teamData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card text-center">
            <p className="text-2xl font-bold text-white">{teamData.team.length}</p>
            <p className="text-sm text-dark-400">Team Members</p>
          </div>
          <div className="card text-center">
            <p className="text-2xl font-bold text-green-400">
              {teamData.team.filter(m => m.timesheetStatus === 'submitted' || m.timesheetStatus === 'approved').length}
            </p>
            <p className="text-sm text-dark-400">Submitted</p>
          </div>
          <div className="card text-center">
            <p className="text-2xl font-bold text-amber-400">
              {teamData.team.filter(m => m.timesheetStatus === 'draft' || (m.timesheetStatus === 'not_started' && m.hoursThisWeek > 0)).length}
            </p>
            <p className="text-sm text-dark-400">In Progress</p>
          </div>
          <div className="card text-center">
            <p className="text-2xl font-bold text-red-400">
              {teamData.team.filter(m => m.timesheetStatus === 'not_started' && m.hoursThisWeek === 0).length}
            </p>
            <p className="text-sm text-dark-400">Not Started</p>
          </div>
        </div>
      )}

      {/* Team list */}
      {teamData && (
        <div className="space-y-3">
          {teamData.team.map((member) => (
            <div key={member.id} className={`card ${getRowColor(member)}`}>
              {/* Summary row — clickable */}
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedId(expandedId === member.id ? null : member.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {getInitials(member.firstName, member.lastName)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-white">{member.firstName} {member.lastName}</p>
                    <p className="text-xs text-dark-500">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <span className={`text-lg font-bold ${
                      member.hoursThisWeek >= 40 ? 'text-accent-400' :
                      member.hoursThisWeek > 0 ? 'text-white' : 'text-dark-500'
                    }`}>
                      {member.hoursThisWeek}h
                    </span>
                  </div>
                  {statusBadge(member.timesheetStatus)}
                  <span className="text-xl">{member.mood ? MOOD_EMOJIS[member.mood] : <span className="text-dark-600 text-sm">—</span>}</span>
                  <svg
                    className={`w-5 h-5 text-dark-400 transition-transform ${expandedId === member.id ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Expanded detail */}
              {expandedId === member.id && (
                <div className="mt-4 pt-4 border-t border-dark-700">
                  {member.checkin ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Mood */}
                      <div className="bg-dark-800 rounded-lg p-4">
                        <p className="text-xs font-medium text-dark-400 uppercase mb-2">Mood</p>
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{member.mood ? MOOD_EMOJIS[member.mood] : '—'}</span>
                          <span className="text-dark-200">{member.mood ? MOOD_LABELS[member.mood] : 'Not set'}</span>
                        </div>
                      </div>

                      {/* Hours summary */}
                      <div className="bg-dark-800 rounded-lg p-4">
                        <p className="text-xs font-medium text-dark-400 uppercase mb-2">Hours</p>
                        <p className="text-2xl font-bold text-white">{member.hoursThisWeek}h <span className="text-sm text-dark-500">/ 40h</span></p>
                        <div className="mt-2 w-full bg-dark-700 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${member.hoursThisWeek >= 40 ? 'bg-accent-400' : 'bg-primary-500'}`}
                            style={{ width: `${Math.min(100, (member.hoursThisWeek / 40) * 100)}%` }}
                          />
                        </div>
                      </div>

                      {/* Wins */}
                      {member.checkin.wins && (
                        <div className="bg-dark-800 rounded-lg p-4">
                          <p className="text-xs font-medium text-dark-400 uppercase mb-2">🎉 Wins</p>
                          <p className="text-dark-200 text-sm whitespace-pre-wrap">{member.checkin.wins}</p>
                        </div>
                      )}

                      {/* Challenges */}
                      {member.checkin.challenges && (
                        <div className="bg-dark-800 rounded-lg p-4">
                          <p className="text-xs font-medium text-dark-400 uppercase mb-2">🤔 Challenges</p>
                          <p className="text-dark-200 text-sm whitespace-pre-wrap">{member.checkin.challenges}</p>
                        </div>
                      )}

                      {/* Help needed */}
                      {member.checkin.helpNeeded && (
                        <div className="bg-dark-800 rounded-lg p-4 md:col-span-2">
                          <p className="text-xs font-medium text-dark-400 uppercase mb-2">🤝 Needs Help With</p>
                          <p className="text-dark-200 text-sm whitespace-pre-wrap">{member.checkin.helpNeeded}</p>
                        </div>
                      )}

                      {/* No content filled */}
                      {!member.checkin.wins && !member.checkin.challenges && !member.checkin.helpNeeded && (
                        <div className="bg-dark-800 rounded-lg p-4 md:col-span-2 text-center">
                          <p className="text-dark-500 text-sm">Check-in submitted but no written responses provided.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-dark-500">No check-in submitted this week</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
